<template>
  <BaseModal
    :modalTitle="$t('area.user.invite.title')"
    :modalIntro="$t('area.user.invite.intro')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSendInvitesClick">
      <UiInputTextAreaVee
        name="emails"
        :label="$t('form.label.email-addresses')"
        :placeholder="$t('form.placeholder.email-addresses')"
        v-model="emailsText"
        @update:model-value="resetValidationMessages" />

      <UiInputSelectVee
        name="role"
        :rules="['required']"
        :label="$t('form.label.role')"
        :options="roleOptions"
        :unselectedText="$t('form.placeholder.role')"
        v-model="selectedRole" />

      <span class="text-sm text-text-secondary">
        {{ $t('area.user.invite.email-count') }}: {{ validEmails.length }}
      </span>

      <InfoText
        :text="validationText"
        class="mt-2 mb-4"
        theme="warning" />

      <template #buttons>
        <UiBaseButton
          :text="$t('action.send-invites')"
          type="submit"
          :isDisabled="!isFormValid"
          :isLoading="isLoading" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { adminApi } from '@/globals/typescript/utils/api/adminClient'
import { useModalStore } from '@/stores/modalStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { useUserStore } from '@/stores/userStore'
import type { TLabAdminRole } from '@team-milva/lab-models/user'
import { union } from 'lodash-es'
import { mapState } from 'pinia'
import isEmail from 'validator/lib/isEmail'

export default {
  name: 'InviteModal',
  emits: ['close'],
  data() {
    return {
      emailsText: '',
      unsavedGuard: useUnsavedChangesGuard(),
      selectedRole: '',
      errorText: '',

      isLoading: false,
    }
  },
  computed: {
    ...mapState(useUserStore, ['allLabAdminUsers', 'currentUser']),
    roleOptions(): Array<{ value: TLabAdminRole; text: string }> {
      return [
        { value: 'admin', text: this.$t('role.admin') },
        { value: 'customer-success', text: this.$t('role.customer-success') },
      ]
    },
    emailList(): string[] {
      return union(
        this.emailsText
          .split(',')
          .map((email) => email.replace(/\s+/g, ''))
          .filter(
            (email) =>
              email.length > 0 &&
              isEmail(email, { allow_utf8_local_part: true })
          )
      )
    },
    existingEmails(): string[] {
      return this.allLabAdminUsers
        .filter((x) => this.emailList.includes(x.email))
        .map((x) => x.email)
    },
    validEmails(): string[] {
      return this.emailList.filter(
        (email) => !this.existingEmails.includes(email)
      )
    },
    validationText(): string {
      if (this.existingEmails.length) {
        return `${this.$t(
          'area.user.invite.message.existing-emails'
        )}: <br> ${this.existingEmails.join(', ')}`
      }

      return ''
    },
    isFormValid(): boolean {
      return this.validEmails.length > 0 && this.selectedRole.length > 0
    },
  },
  methods: {
    async onSendInvitesClick() {
      this.resetValidationMessages()
      this.isLoading = true

      try {
        const result = await adminApi('post', '/invites', {
          body: {
            userID: this.currentUser?.id ?? '',
            emails: this.validEmails,
            role: this.selectedRole,
          },
        })

        if (result.success) {
          useToast().success(this.$t('area.user.invite.success.invited'))
          this.$emit('close')
          return
        }

        this.errorText = this.$t('error.common.standard')
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },
    resetValidationMessages() {
      this.errorText = ''
    },
  },
  created() {
    // Last in created(), after the form is populated
    this.unsavedGuard.register({ modalId: useModalStore().top?.id, snapshot: () => ({ emailsText: this.emailsText, selectedRole: this.selectedRole }) })
  },
  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

